import API from '../config/api';
import React, { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import axios from 'axios';

/*
  Mobile speed-dial: a single floating button that fans out three actions —
  Alerts (notifications), ShopGenie (AI assistant) and Help (support chat).
  Each action triggers the existing feature: Alerts / Help fire a window
  event picked up by NotificationLauncher / ChatBot; ShopGenie navigates.
  Desktop keeps its own individual launchers; this is mobile-only.
*/
function MobileFabDial() {
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();

  const userInfo = JSON.parse(localStorage.getItem('userInfo') || 'null');

  /* Small unread badge on the main button */
  useEffect(() => {
    if (!userInfo?.email) return;

    axios
      .get(`${API}/notifications/${userInfo.email}`)
      .then(({ data }) =>
        setUnread(data.filter((n) => !n.isRead).length)
      )
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.email]);

  /* Stay in sync when notifications are read in the sheet */
  useEffect(() => {
    const sync = (e) => setUnread(e.detail);
    window.addEventListener('unread-count-changed', sync);
    return () => window.removeEventListener('unread-count-changed', sync);
  }, []);

  /* Hide the dial while the help chat is open (it would cover the send button) */
  useEffect(() => {
    const toggle = (e) => {
      setHidden(e.detail);
      if (e.detail) setOpen(false);
    };
    window.addEventListener('help-chat-toggle', toggle);
    return () => window.removeEventListener('help-chat-toggle', toggle);
  }, []);

  const actions = [
    {
      key: 'alerts',
      icon: '🔔',
      label: 'Alerts',
      cls: 'fd-alerts',
      run: () => window.dispatchEvent(new Event('open-notifications'))
    },
    {
      key: 'genie',
      icon: '🧞',
      label: 'ShopGenie',
      cls: 'fd-genie',
      run: () => navigate('/assistant')
    },
    {
      key: 'help',
      icon: '💬',
      label: 'Help',
      cls: 'fd-help',
      run: () => window.dispatchEvent(new Event('open-help-chat'))
    }
  ];

  const handle = (run) => {
    setOpen(false);
    run();
  };

  return (
    <div className={'fab-dial' + (hidden ? ' fab-dial-off' : '')}>
      {open && <div className="fab-dial-backdrop" onClick={() => setOpen(false)} />}

      <div className={'fab-dial-actions' + (open ? ' show' : '')}>
        {actions.map((a, i) => (
          <button
            key={a.key}
            type="button"
            className={'fab-dial-action ' + a.cls}
            style={{ transitionDelay: (open ? i * 45 : 0) + 'ms' }}
            onClick={() => handle(a.run)}
            tabIndex={open ? 0 : -1}
          >
            <span className="fab-dial-label">{a.label}</span>
            <span className="fab-dial-ic">
              {a.icon}
              {a.key === 'alerts' && unread > 0 && (
                <span className="fab-dial-badge">{unread}</span>
              )}
            </span>
          </button>
        ))}
      </div>

      <button
        type="button"
        className={'fab-dial-main' + (open ? ' open' : '')}
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? 'Close menu' : 'Open quick actions'}
      >
        <span className="fab-dial-main-ic">＋</span>
        {!open && unread > 0 && (
          <span className="fab-dial-badge fab-dial-badge-main">{unread}</span>
        )}
      </button>

      <style>{dialStyles}</style>
    </div>
  );
}

const dialStyles = `
  .fab-dial { display: none; }

  @media (max-width: 1199.98px) {
    .fab-dial { display: block; }
    .fab-dial-off .fab-dial-main,
    .fab-dial-off .fab-dial-actions,
    .fab-dial-off .fab-dial-backdrop { display: none !important; }

    .fab-dial-backdrop {
      position: fixed;
      inset: 0;
      z-index: 1048;
      background: rgba(15, 23, 42, 0.4);
      animation: fdFade 0.2s ease;
    }
    @keyframes fdFade { from { opacity: 0; } to { opacity: 1; } }

    .fab-dial-main {
      position: fixed;
      z-index: 1052;
      right: 16px;
      bottom: calc(80px + env(safe-area-inset-bottom, 0px));
      width: 58px;
      height: 58px;
      border: none;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      background: linear-gradient(135deg, #ea580c, #f59e0b);
      box-shadow: 0 10px 26px rgba(234, 88, 12, 0.5);
      cursor: pointer;
    }
    .fab-dial-main-ic {
      font-size: 30px;
      line-height: 1;
      transition: transform 0.28s ease;
    }
    .fab-dial-main.open {
      background: linear-gradient(135deg, #3b0764, #9a3412);
    }
    .fab-dial-main.open .fab-dial-main-ic { transform: rotate(135deg); }

    .fab-dial-actions {
      position: fixed;
      z-index: 1052;
      right: 16px;
      bottom: calc(150px + env(safe-area-inset-bottom, 0px));
      display: flex;
      flex-direction: column-reverse;
      align-items: flex-end;
      gap: 14px;
      pointer-events: none;
    }
    .fab-dial-action {
      display: flex;
      align-items: center;
      gap: 10px;
      border: none;
      background: transparent;
      padding: 0;
      cursor: pointer;
      opacity: 0;
      transform: translateY(18px) scale(0.8);
      transition: opacity 0.2s ease, transform 0.2s ease;
      pointer-events: none;
    }
    .fab-dial-actions.show .fab-dial-action {
      opacity: 1;
      transform: translateY(0) scale(1);
      pointer-events: auto;
    }
    .fab-dial-label {
      background: #fff;
      font-weight: 800;
      font-size: 0.74rem;
      padding: 6px 12px;
      border-radius: 20px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
      white-space: nowrap;
    }
    .fab-dial-ic {
      position: relative;
      width: 50px;
      height: 50px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 22px;
      color: #fff;
      border: 2px solid rgba(255, 255, 255, 0.92);
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25);
    }
    .fd-alerts .fab-dial-ic { background: linear-gradient(135deg, #b45309, #f59e0b); }
    .fd-genie .fab-dial-ic {
      background:
        radial-gradient(circle at 50% 38%, rgba(253,230,138,0.5), transparent 62%),
        linear-gradient(135deg, #6d28d9, #ea580c);
      border-color: rgba(253,230,138,0.9);
    }
    .fd-help .fab-dial-ic { background: linear-gradient(135deg, #ea580c, #f59e0b); }
    .fd-alerts .fab-dial-label { color: #b45309; }
    .fd-genie .fab-dial-label { color: #6d28d9; }
    .fd-help .fab-dial-label { color: #c2410c; }

    .fab-dial-badge {
      position: absolute;
      top: -5px;
      right: -5px;
      min-width: 19px;
      height: 19px;
      padding: 0 5px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #dc2626;
      color: #fff;
      font-size: 0.62rem;
      font-weight: 700;
      border-radius: 20px;
      box-shadow: 0 0 0 2px #fff;
    }
    .fab-dial-badge-main { top: -3px; right: -3px; }
  }
`;

export default MobileFabDial;
